import type { FieldMetaData } from "./fieldsMetaData";
import { createFieldMetaData } from "./createFieldMetaData";
import { Address, convertToPhoneNumbers } from "./companyFields";
import { findCountryById, findStateById } from "../countrystateutils";
import moment from "moment";

export interface ContactFormState {
  isDeleted: boolean;
  isValidated: boolean;
  clientId: number;
  contactId: number;

  firstName: string;
  insertion: string;
  lastName: string;
  emailAddresses: string;
  isEmailBlacklisted: boolean;
  mobileNumbers: string;
  gender: server.dto.Genders;
  position: string;
  department: string;
  culture: server.dto.LCID;
  landlineNumbers: string;
  salutation: string;
  attn: string;
  homeAddress: Address;
  ianaTimeZoneId: string;
  status: server.dto.StatusSmall;
  owner: server.dto.UserSmall;
  tags: server.dto.TagSmall[];

  twitter: string;
  website: string;
  facebook: string;
  networkPath: string;

  socialSecurity: string;
  birthdate: moment.Moment;
  birthplace: string;
  maritalStatus: server.dto.MaritalStatuses;
  additionalInformation: string;

  nationality: string
  passportName: string;
  passportNumber: string;
  passportIssued: moment.Moment;
  passportExpiration: moment.Moment;
}

export const getInstruction = (contactForm: ContactFormState, partial: Partial<server.dto.UpdateContactDetailsInstruction>): server.dto.UpdateContactDetailsInstruction => ({
  additional: contactForm.additionalInformation,
  address: {
    addressLine1: contactForm.homeAddress.addressLine1,
    addressLine2: contactForm.homeAddress.addressLine2,
    city: contactForm.homeAddress.city,
    postalCode: contactForm.homeAddress.postalCode,
    stateId: contactForm.homeAddress.state.key,
  },
  attn: contactForm.attn,
  birthdate: contactForm.birthdate?.toDate(),
  birthplace: contactForm.birthplace,
  contactId: contactForm.contactId,
  culture: contactForm.culture,
  department: contactForm.department,
  emailAddresses: contactForm.emailAddresses,
  emailBlacklisted: contactForm.isEmailBlacklisted,
  facebook: contactForm.facebook,
  landlineNumbers: convertToPhoneNumbers(contactForm.landlineNumbers),
  maritalStatus: contactForm.maritalStatus ?? "unknown",
  mobileNumbers: convertToPhoneNumbers(contactForm.mobileNumbers),
  name: {
    firstName: contactForm.firstName,
    insertion: contactForm.insertion,
    lastName: contactForm.lastName,
  },
  nationality: contactForm.nationality,
  networkPath: contactForm.networkPath,
  ownerUserId: contactForm.owner.userId,
  passportExpiration: contactForm.passportExpiration?.toDate(),
  passportIssued: contactForm.passportIssued?.toDate(),
  passportName: contactForm.passportName,
  passportNumber: contactForm.passportNumber,
  position: contactForm.position,
  salutation: contactForm.salutation,
  socialSecurity: contactForm.socialSecurity,
  statusId: contactForm.status.statusId,
  twitter: contactForm.twitter,
  website: contactForm.website,
  gender: contactForm.gender,
  ...partial
});

export const contactToFormState = (contact: server.dto.Contact, serverReference: server.dto.ServerReference): ContactFormState => ({
  isDeleted: contact.isDeleted,
  isValidated: contact.isValidated,
  clientId: contact.clientId,
  contactId: contact.contactId,

  firstName: contact.name.firstName ?? "",
  insertion: contact.name.insertion ?? "",
  lastName: contact.name.lastName ?? "",
  emailAddresses: contact.emailAddresses.join(";") ?? "",
  isEmailBlacklisted: contact.emailBlacklisted,
  mobileNumbers: contact.mobileNumbers.join(";") ?? "",
  gender: contact.gender,
  position: contact.position ?? "",
  department: contact.department ?? "",
  culture: contact.culture,
  landlineNumbers: contact.landlineNumbers.join(";") ?? "",
  salutation: contact.salutation ?? "",
  tags: contact.tags,
  homeAddress: {
    addressLine1: contact.address.addressLine1 ?? "",
    addressLine2: contact.address.addressLine2 ?? "",
    postalCode: contact.address.postalCode ?? "",
    city: contact.address.city ?? "",
    // @ts-ignore
    state: findStateById(contact.address.stateId, serverReference),
    // @ts-ignore
    country: findCountryById(contact.address.country, serverReference),
    dto: contact.address
  },
  ianaTimeZoneId: contact.ianaTimeZoneId,
  status: contact.status,
  owner: contact.owner,
  twitter: contact.twitter ?? "",
  website: contact.website ?? "",
  networkPath: contact.networkPath ?? "",
  facebook: contact.facebook ?? "",
  socialSecurity: contact.socialSecurity ?? "",
  // @ts-ignore
  birthdate: contact.birthdate ? moment.utc(contact.birthdate) : undefined,
  birthplace: contact.birthPlace ?? "",
  maritalStatus: contact.maritalStatus,
  additionalInformation: contact.additional ?? "",
  nationality: contact.nationality ?? "",
  passportName: contact.passport?.name ?? "",
  passportNumber: contact.passport?.number ?? "",
  // @ts-ignore
  passportIssued: contact.passport?.issued ? moment.utc(contact.passport?.issued) : undefined,
  // @ts-ignore
  passportExpiration: contact.passport?.expiration ? moment.utc(contact.passport?.expiration) : undefined,
  attn: contact.attn ?? "",
})

export interface ContactFieldMetaData {
    deleted: FieldMetaData;
    isValidated: FieldMetaData;

    name: FieldMetaData;
    email: FieldMetaData;
    landlineNumbers: FieldMetaData;
    mobileNumbers: FieldMetaData;
    status: FieldMetaData;
    culture: FieldMetaData;
    gender: FieldMetaData;
    position: FieldMetaData;
    department: FieldMetaData;
    tags: FieldMetaData;
    salutation: FieldMetaData;
    attn: FieldMetaData;
    address: FieldMetaData;
    localTime: FieldMetaData;
    owner: FieldMetaData;

    website: FieldMetaData;
    twitter: FieldMetaData;
    facebook: FieldMetaData;
    networkPath: FieldMetaData;

    socialSecurity: FieldMetaData;
    birthdate: FieldMetaData;
    birthplace: FieldMetaData;
    maritalStatus: FieldMetaData;
    additional: FieldMetaData;

    nationality: FieldMetaData;
    passportName: FieldMetaData;
    passportNumber: FieldMetaData;
    passportIssued: FieldMetaData;
    passportExpiration: FieldMetaData;
}

export const getContactFieldMetaData = (
    environment: server.dto.SelectedEnvironment,
    item: ContactFormState,
    itemPermissions: server.dto.ContactObjectPermissions,
    hasTags: boolean,
    hasMultipleStatuses: boolean
): ContactFieldMetaData => ({
    deleted: createFieldMetaData(
        false,
        !item.isDeleted,
        true,
    ),

    isValidated: createFieldMetaData(
        false,
        item.isValidated,
        !itemPermissions.canModify,
    ),

    name: createFieldMetaData(
        false,
        false, 
        !itemPermissions.canModify,
    ),

    email: createFieldMetaData(
        !item.emailAddresses?.length,
        !item.emailAddresses?.length && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    landlineNumbers: createFieldMetaData(
        !item.landlineNumbers?.length,
        !item.landlineNumbers?.length && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    mobileNumbers: createFieldMetaData(
        !item.mobileNumbers?.length,
        !item.mobileNumbers?.length && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),
    
    status: createFieldMetaData(
        false,
        environment.type === "external" || !hasMultipleStatuses, //Hide when external or only one status
        !itemPermissions.canModify,
    ),

   culture: createFieldMetaData(
        false,
        false,
        !itemPermissions.canModify,
    ),

    gender: createFieldMetaData(
        item.gender === "unspecified",
        (item.gender === "unspecified" && !itemPermissions.canModify),
        environment.type === "external" || !itemPermissions.canModify,
    ),
    
    position: createFieldMetaData(
        !item.position,
        (!item.position && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    department: createFieldMetaData(
        !item.department,
        (!item.department && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    tags: createFieldMetaData(
        !item.tags || item.tags.length === 0,
        !hasTags || environment.type === "external" || ((!item.tags || item.tags.length === 0) && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    salutation: createFieldMetaData(
        !item.salutation,
        (!item.salutation && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    attn: createFieldMetaData(
        !item.attn,
        (!item.attn && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    address: createFieldMetaData(
        item.homeAddress.dto.formatted?.length === 0,
        environment.type === "external", 
        !itemPermissions.canModify,
    ),

    localTime: createFieldMetaData(
        !item.ianaTimeZoneId,
        environment.type === "external",
        true, //Cannot modify
    ),

    owner: createFieldMetaData(
        !item.owner,
        environment.type === "external", //Dont display to external users,
        !itemPermissions.canModify,
    ),

    website: createFieldMetaData(
        !item.website,
        (!item.website && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    twitter: createFieldMetaData(
        !item.twitter,
        (!item.twitter && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    facebook: createFieldMetaData(
        !item.facebook,
        (!item.facebook && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    networkPath: createFieldMetaData(
        !item.networkPath,
        (!item.networkPath && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    socialSecurity: createFieldMetaData(
        !item.socialSecurity,
        environment.type === "external" || (!item.socialSecurity && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    birthdate: createFieldMetaData(
        !item.birthdate,
        environment.type === "external"|| (!item.birthdate && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    birthplace: createFieldMetaData(
        !item.birthplace,
        environment.type === "external" || (!item.birthplace && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    maritalStatus: createFieldMetaData(
        item.maritalStatus === "unknown",
        environment.type === "external" || (item.maritalStatus === "unknown" && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    additional: createFieldMetaData(
        !item.additionalInformation,
        environment.type === "external" || (!item.additionalInformation && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    nationality: createFieldMetaData(
        !item.nationality,
        (!item.nationality && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    passportName: createFieldMetaData(
        !item.passportName,
        (!item.passportName && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    passportNumber: createFieldMetaData(
        !item.passportNumber,
        (!item.passportNumber && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    passportIssued: createFieldMetaData(
        !item.passportIssued,
        (!item.passportIssued && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    passportExpiration: createFieldMetaData(
        !item.passportExpiration,
        (!item.passportExpiration && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),
});